/**
 * Weather impact on an antenna from an NWS gridpoint forecast, ported from the
 * pure part of loader-weather.js (_computeWeatherImpact): Optical antennas by
 * cloud cover, Ka-band by precipitation probability, everything else
 * 0.02 + precipitation / 1000. The fetching stays in the app.
 */
import type { Antenna } from '@/features/loader/types';
import type { WeatherImpact } from './types';

export interface NwsSeries {
  values?: { value: number | null }[];
}

/** The parts of api.weather.gov gridpoints/<office>/<x>,<y> the impact reads. */
export interface NwsGridData {
  properties?: {
    skyCover?: NwsSeries;
    probabilityOfPrecipitation?: NwsSeries;
    windSpeed?: NwsSeries;
  };
}

/** First value of an NWS time series, 0 when absent or null. */
export function latestValue(series: NwsSeries | undefined): number {
  if (!series || !series.values || series.values.length === 0) return 0;
  return series.values[0].value || 0;
}

/** Clear / Partial / Overcast / Heavy cloud by cover, plus ' + Rain' above 50% precipitation. */
export function describeConditions(cloudCover: number | null | undefined, precipProb: number): string {
  if (cloudCover === null || cloudCover === undefined) return 'Unknown';
  let desc: string;
  if (cloudCover < 20) desc = 'Clear';
  else if (cloudCover < 50) desc = 'Partial';
  else if (cloudCover < 80) desc = 'Overcast';
  else desc = 'Heavy cloud';
  if (precipProb > 50) desc += ' + Rain';
  return desc;
}

/** Live impact for an antenna from its gridpoint forecast. */
export function impactFromForecast(antenna: Antenna, gridData: NwsGridData): WeatherImpact {
  const props = gridData.properties || {};
  const cloudCover = latestValue(props.skyCover);
  const precipProb = latestValue(props.probabilityOfPrecipitation);
  const windSpeed = latestValue(props.windSpeed);

  const isOptical = antenna.capabilities.bands.indexOf('Optical') !== -1;
  const isKaBand = antenna.capabilities.bands.indexOf('Ka-band') !== -1;

  let impactFactor: number;
  if (isOptical) {
    if (cloudCover > 80) impactFactor = 0.9;
    else if (cloudCover > 50) impactFactor = 0.6;
    else if (cloudCover > 30) impactFactor = 0.2;
    else impactFactor = 0.05;
  } else if (isKaBand) {
    if (precipProb > 80) impactFactor = 0.4;
    else if (precipProb > 50) impactFactor = 0.2;
    else impactFactor = 0.05;
  } else {
    impactFactor = 0.02 + precipProb / 1000;
  }

  return {
    impactFactor,
    cloudCover,
    precipProb,
    windSpeed,
    conditions: describeConditions(cloudCover, precipProb),
    source: 'live',
  };
}

/** Fallback impact from the antenna's static weatherImpactFactor (0.05 when unset). */
export function staticImpact(antenna: Antenna, conditions = 'N/A'): WeatherImpact {
  return {
    impactFactor: antenna.performance?.weatherImpactFactor || 0.05,
    cloudCover: null,
    precipProb: null,
    conditions,
    source: 'static',
  };
}

/** NWS covers US territory only: the continental/Alaska box or country 'USA'. */
export function isUsLocation(antenna: Antenna): boolean {
  const { lat, lon, country } = antenna.location;
  return (lat > 17 && lat < 72 && lon > -180 && lon < -60) || country === 'USA';
}
